<template>
  <b-card class="mb-3">
    <template #header>クーポンコード</template>

    <template v-if="availableCouponListDisplay && availableCoupons.length">
      <div class="fw-bold mb-2">ご利用可能なクーポン</div>

      <div v-for="coupon in availableCoupons" :key="coupon.id" class="coupon-item border p-3 mb-3">
        <div class="d-flex align-items-start justify-content-between">
          <div>
            <div class="fw-bold fs-6">{{ discountLabel(coupon) }}</div>
            <div class="mt-1">{{ expiryLabel(coupon) }}</div>
            <div class="mt-1">クーポン名: {{ coupon.name }}</div>
          </div>
          <button
            v-if="isApplied(coupon)"
            type="button"
            class="coupon-applied-badge text-nowrap"
            @click="removeCoupon"
            :name="`remove_coupon_${coupon.id}`"
            :id="`remove_coupon_${coupon.id}`"
          >
            適用中
          </button>
          <button
            v-else
            type="button"
            class="coupon-apply-btn text-nowrap"
            @click="applyCouponCode(coupon.code)"
            :name="`apply_coupon_${coupon.id}`"
            :id="`apply_coupon_${coupon.id}`"
          >
            適用する
          </button>
        </div>
      </div>
    </template>

    <div class="fw-bold mb-2">クーポンコードを入力してください。</div>
    <input
      type="text"
      class="form-control coupon-input"
      v-model="couponCodeInput"
      placeholder="例）SALE2026"
      name="coupon_code"
      id="coupon_code"
      @input="syncCouponCode"
    />
  </b-card>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useField } from "vee-validate";

import { useCartStore } from "@/stores/cart";

import { AvailableCouponType } from "@/types/UrlType";

interface Props {
  availableCouponListDisplay: boolean;
  availableCoupons: AvailableCouponType[];
}

defineProps<Props>();

const cartStore = useCartStore();

const { value: couponCode } = useField<string>("couponCode");

const couponCodeInput = ref(couponCode.value || "");

const discountLabel = (coupon: AvailableCouponType) => {
  if (coupon.discountAmount) return `${coupon.discountAmount}円 OFF`;
  if (coupon.discountRatio) return `${coupon.discountRatio}% OFF`;
  return "";
};

const expiryLabel = (coupon: AvailableCouponType) => {
  if (!coupon.eDate) return "無期限";

  const date = new Date(coupon.eDate);
  const pad = (value: number) => String(value).padStart(2, "0");
  const time = `${date.getHours()}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${time}まで有効`;
};

const isApplied = (coupon: AvailableCouponType) => coupon.code === couponCode.value;

// 適用する = コードを入力欄へ反映して保持するだけ。
// 検証・割引計算は購入時 (ordersValidate / ordersCreate) に行う。
const applyCouponCode = (code: string) => {
  if (!code) return;

  couponCodeInput.value = code;
  couponCode.value = code;
  cartStore.cart.couponCode = code;
};

// 入力した内容をそのまま保持する（空にすればクーポン解除）
const syncCouponCode = () => {
  couponCode.value = couponCodeInput.value;
  cartStore.cart.couponCode = couponCodeInput.value;
};

// 適用中バッジをクリックでクーポン解除
const removeCoupon = () => {
  couponCodeInput.value = "";
  couponCode.value = "";
  cartStore.cart.couponCode = "";
};
</script>

<style lang="scss" scoped>
.coupon-input {
  max-width: 280px;
}
.coupon-item {
  border-radius: 4px;
}
.coupon-apply-btn {
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #2962ff;
  background-color: #fff;
  border: 1px solid #2962ff;
  border-radius: 4px;
  transition: opacity 0.15s ease;

  &:hover,
  &:focus {
    color: #2962ff;
    background-color: #fff;
    opacity: 0.7;
  }
}
.coupon-applied-badge {
  display: inline-block;
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #1e9e54;
  background-color: #e6f7ed;
  border: none;
  border-radius: 4px;
  transition: opacity 0.15s ease;

  &:hover,
  &:focus {
    opacity: 0.7;
  }
}
</style>
